import { GovTag, GovTooltip } from '@gov-design-system-ce/react';
import type { ExtraConfig } from '@gov-nx/core/app';
import { useLocale } from '@gov-nx/core/service';
import { filter, hasProperty, pipe } from '@gov-nx/core/types';
import { useDataBoxLabel } from '@gov-nx/module/data-box';
import {
	RosComplaintVariant,
	useProfilROSDetailContext,
} from '@gov-nx/module/page';
import { ServiceCode } from '@gov-nx/module/service';
import {
	Button,
	ButtonGroupLayout,
	ContentLayout,
	DataBoxConnectBanner,
	DataTable,
	Icon,
	LayoutGap,
} from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';
import { EstablishmentsListModal } from './EstablishmentsListModal';

export const PFODataView = (
	props: ExtraConfig['profileRoutes']['rosDetailConfig']['rosDetail']['PFODataView']
) => {
	const {
		detailData,
		setControls,
		code,
		name,
		areAnyConnected,
		onService,
		members,
		persons,
		establishments,
		isMembersListModalOpen,
		isPersonsListModalOpen,
		isEstablishmentsListModalOpen,
	} = useProfilROSDetailContext();
	const { t } = useLocale(code);

	const { label } = useDataBoxLabel();

	return (
		<>
			<ContentLayout>
				<LayoutGap gap="m">
					<h3 className="text-(--text-secondary) text-body-l">
						{t('nadpis.zakladni-informace')}
					</h3>
					<DataTable responsive>
						<tbody>
							<tr>
								<td>{t('zaznam.ico')}</td>
								<td>
									<strong>{detailData?.ico ? detailData.ico : '-'}</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.pravni-forma')}</td>
								<td>
									<strong>
										{detailData?.nazevPravniFormy
											? detailData.nazevPravniFormy
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.stav')}</td>
								<td>
									<strong>
										{detailData?.pravniStav?.nazev
											? detailData.pravniStav.nazev
											: '-'}
									</strong>
								</td>
							</tr>
						</tbody>
					</DataTable>
					{props.isComplaintCtaVisible ? (
						<ButtonGroupLayout>
							<Button
								onClick={() =>
									setControls({
										complaintModal: detailData,
										complaintVariant: RosComplaintVariant.BASIC_INFO,
									})
								}
								color="primary"
								type="outlined"
								size="l">
								{t('akce.podnet-k-oprave-udaju')}
							</Button>
						</ButtonGroupLayout>
					) : null}
				</LayoutGap>
			</ContentLayout>
			<ContentLayout>
				<LayoutGap gap="m">
					<div className="flex items-center gap-4">
						<h3 className="text-(--text-secondary) text-body-l">
							{t('nadpis.agendova-cast')}
						</h3>
						{detailData?.pozastaveniPreruseni ? (
							<GovTag
								color="neutral"
								size="xs">
								{t('tag.pozastaveni-preruseni')}
							</GovTag>
						) : null}
					</div>

					<DataTable responsive>
						<tbody>
							<tr>
								<td>{t('zaznam.nazev')}</td>
								<td>
									<strong>{name ? name : '-'}</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.agenda')}</td>
								<td>
									<strong>
										{detailData?.agenda?.nazev ? detailData.agenda.nazev : '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.agenda-kod')}</td>
								<td>
									<strong>
										{detailData?.agenda?.kod ? detailData.agenda.kod : '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.zapisujici-organ')}</td>
								<td>
									<strong>
										{detailData?.pravniStav?.nazev
											? detailData.pravniStav.nazev
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.sidlo')}</td>
								<td>
									<strong>
										{detailData?.sidloSpolecnosti?.adresaText
											? detailData?.sidloSpolecnosti?.adresaText
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.datum-vzniku')}</td>
								<td>
									<strong>
										{detailData?.datumVzniku
											? toDateReadable(detailData.datumVzniku)
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.datum-zaniku')}</td>
								<td>
									<strong>
										{detailData?.datumZaniku
											? toDateReadable(detailData.datumZaniku)
											: t('zaznam.datum-zaniku-ne')}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.pozastaveni-preruseni')}</td>
								<td>
									<strong>
										{detailData?.pozastaveniPreruseni
											? t('zaznam.pozastaveni-preruseni-ano')
											: t('zaznam.pozastaveni-preruseni-ne')}
									</strong>
								</td>
							</tr>
							{members.length ? (
								<tr>
									<td>{t('zaznam.struktura-statutarniho-organu')}</td>
									<td>
										<strong>{t('zaznam.seznam-clenu')}</strong>
										<Button
											onClick={isMembersListModalOpen.setTrue}
											color="primary"
											type="base"
											size="s">
											<Icon
												type="components"
												name="eye"
											/>
										</Button>
									</td>
								</tr>
							) : null}
							{establishments.length ? (
								<tr>
									<td>{t('zaznam.provozovny')}</td>
									<td>
										<strong>{t('zaznam.seznam-provozoven')}</strong>
										<Button
											onClick={isEstablishmentsListModalOpen.setTrue}
											color="primary"
											type="base"
											size="s">
											<Icon
												type="components"
												name="eye"
											/>
										</Button>
										<EstablishmentsListModal />
									</td>
								</tr>
							) : null}

							{detailData?.dorucovaciAdresa?.adresaText ? (
								<>
									<tr className="border-b-0">
										<td>{t('zaznam.dorucovaci-adresa-popis')}</td>
										<td>
											<strong>{detailData.dorucovaciAdresa.adresaText}</strong>
										</td>
									</tr>
									<tr className="border-b-0">
										<td colSpan={2}>
											<p className="text-body-xs! text-(--text-secondary)">
												{t('zaznam.dorucovaci-adresa-vysvetleni')}
											</p>
										</td>
									</tr>
								</>
							) : null}
							{persons.length ? (
								<tr>
									<td>{t('zaznam.angazovane-osoby')}</td>
									<td>
										<strong>{t('zaznam.seznam-osob')}</strong>
										<Button
											onClick={isPersonsListModalOpen.setTrue}
											color="primary"
											type="base"
											size="s">
											<Icon
												type="components"
												name="eye"
											/>
										</Button>
									</td>
								</tr>
							) : null}
						</tbody>
					</DataTable>
					{props.isComplaintCtaVisible ? (
						<ButtonGroupLayout>
							<Button
								onClick={() =>
									setControls({
										complaintModal: detailData,
										complaintVariant: RosComplaintVariant.AGENDA,
									})
								}
								color="primary"
								type="outlined"
								size="l">
								{t('akce.podnet-k-oprave-udaju')}
							</Button>
						</ButtonGroupLayout>
					) : null}
				</LayoutGap>
			</ContentLayout>
			<ContentLayout>
				<LayoutGap gap="m">
					<h3 className="text-(--text-secondary) text-body-l">
						{t('nadpis.datove-schranky')}
					</h3>

					{pipe(
						detailData?.datoveSchranky,
						filter(hasProperty('id')),
						(databoxes) =>
							databoxes.length ? (
								<DataTable responsive>
									<tbody>
										{databoxes.map((databox) => (
											<tr key={databox.id}>
												<td>
													{databox.typ
														? label(databox.typ)
														: t('zaznam.datova-schranka')}
												</td>
												<td>
													<strong>{databox.id}</strong>
												</td>
											</tr>
										))}
									</tbody>
								</DataTable>
							) : (
								<DataBoxConnectBanner
									requiredTypes={[]}
									translationPrefix="banner"
								/>
							)
					)}
				</LayoutGap>
			</ContentLayout>
			<ContentLayout>
				<LayoutGap gap="m">
					<h3 className="text-(--text-secondary) text-body-l">
						{t('nadpis.vypis.zivnostenskeho.rejstriku')}
					</h3>
					<p className="text-(--text-secondary) text-s">
						{t('nadpis.vypis.zivnostenskeho.rejstriku-popis')}
					</p>
				</LayoutGap>

				<ButtonGroupLayout>
					{areAnyConnected ? (
						<Button
							onClick={() =>
								onService(ServiceCode['vypis-z-zivnostenskeho-rejstriku'])
							}
							color="primary"
							size="l"
							type="solid">
							{t('akce.pozadat-o-vypis')}
						</Button>
					) : (
						<GovTooltip>
							<GovTooltip.Trigger className="border-b-0">
								<Button
									isDisabled
									color="primary"
									size="m"
									type="solid">
									{t('akce.pozadat-o-vypis')}
								</Button>
							</GovTooltip.Trigger>
							<GovTooltip.Content>
								<p>{t('akce.nejprve-pripojte')}</p>
							</GovTooltip.Content>
						</GovTooltip>
					)}
				</ButtonGroupLayout>
			</ContentLayout>
		</>
	);
};
